'use client';

import React, { useEffect, useState } from 'react';
import type { SkuCardProps } from '@/types/props/components/skus';
import { faHeart as solidFaHeart } from '@fortawesome/free-solid-svg-icons';
import { faHeart } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import imageLoader from '@/utilites/imageLoader';
import SkuCardDescription from './SkuCardDescription';
import { clsx } from 'clsx';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'next-i18next';
import useWishlist from '@/hooks/useWishlist';
import type { SkuDTO } from '@/types/api/sku';
import { SkuMainImage } from '@/utilites/get-sku-main-image';
import { useAnalytics } from '@/hooks/useAnalytics';
import useEvents from '@/hooks/useEvents';
import usePendingNavigation from '@/hooks/usePendingNavigation';
import SkuTrendingBadge from '@/components/sku/SkuTrendingBadge';
import { useRouter } from 'next/router';
import CommonTooltip from '@/components/common/CommonTooltip';
import { toDe } from '@/utilites/sku-operations';
import localePath from '@/utilites/language/locale-path';
import {
  END_OF_SUMMER_SALE_PATH,
  END_OF_SUMMER_SALE_QUERY_FLAG,
  END_OF_SUMMER_SALE_SKU_TAG,
  IS_EASTER_ENABLED,
  IS_END_OF_SUMMER_SALE_ENABLED,
} from '@/constants/campaigns.constants';
import {
  SkuImageSkeleton,
  SkuNameSkeleton,
  SkuDescriptionSkeleton,
  SkuPriceSkeleton,
  WishlistButtonSkeleton,
} from './desktop/SkuCardDesktopSkeleton';
import SkuCardImage from './desktop/SkuCardImageDesktop';
import { CommonEnum } from '@/types/translations/CommonEnum';
import SkuPriceDisplay from '@/components/sku/Common/SkuPriceDisplay';

import { SkuEnum } from '@/types/translations/SkuEnum';

const CONTAIN_STYLE = { width: '100%', height: '100%', objectFit: 'contain' } as const;

export default function SkuCard({
  sku,
  showHighestBid,
  askValue,
  isSellerCard = false,
  onCardClick,
}: SkuCardProps) {
  const [releaseDate, setReleaseDate] = useState<string | null>(null);
  const [isNavigating, setIsNavigating] = usePendingNavigation();
  const { isInWishlist, toggleFromWishlist } = useWishlist();
  const { t: tSku } = useTranslation('sku');
  const { getPageType } = useAnalytics();
  const { fireGlobalToBottleButtonClickEvent } = useEvents('', []);
  const router = useRouter();
  const locale = router.locale;
  const onEasterPage = IS_EASTER_ENABLED && router?.pathname === '/easter-sale-2026';
  // The campaign covers a bottle either because the visitor is on the campaign
  // page or because marketing tagged the bottle in Strapi (WEB-1455).
  const inEndOfSummerSale =
    IS_END_OF_SUMMER_SALE_ENABLED &&
    (router?.pathname === END_OF_SUMMER_SALE_PATH ||
      !!sku?.sku_tags?.tags?.includes(END_OF_SUMMER_SALE_SKU_TAG));
  const { t: tCommon } = useTranslation('common');

  // Rabatt-Badge: Bedingungen und Prozentzahl berechnen
  const discount = React.useMemo(() => {
    if (!sku) return null;
    const hasRetail = sku.retailPrice != null && sku.retailPrice > 0;
    // displayPrice wie in der Preis-Anzeige bestimmen:
    // - Seller Card: askValue
    // - Normale Karte: sku.lowestAsk (außer wenn HighestBid angezeigt werden soll)
    const displayPrice = isSellerCard
      ? askValue ?? null
      : showHighestBid
        ? null
        : sku.lowestAsk ?? null;

    const hasDisplay = displayPrice != null && displayPrice > 0;
    const onBlackFridayPage = router?.pathname === '/Black-Friday-2025';
    const isSpecialEventContext =
      onBlackFridayPage || onEasterPage || inEndOfSummerSale || sku.hot_deal;
    // Nur Rabatt anzeigen, wenn wirklich ein durchgestrichener Preis vorliegt
    if (isSpecialEventContext && hasRetail && hasDisplay && sku.retailPrice! > (displayPrice as number)) {
      const percent = Math.round(((sku.retailPrice! - (displayPrice as number)) / sku.retailPrice!) * 100);
      return isFinite(percent) && percent > 0 ? percent : null;
    }
    return null;
  }, [sku, isSellerCard, askValue, showHighestBid, onEasterPage, inEndOfSummerSale, router?.pathname]);

  const handleSkuCardClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    if (sku) {
      if (onCardClick) {
        onCardClick(e, sku);
      } else {
        fireGlobalToBottleButtonClickEvent({
          slug: sku.slug,
          product_name: sku.name || '',
          product_category: sku?.category?.name || '',
          product_price: sku.lowestAsk || 0,
          page_type: getPageType(),
          price_ask: sku.lowestAsk || 0,
          brand_name_page: sku?.brand?.name || '',
        });
        // If we are on the Black Friday or Easter page, forward a URL flag so the product page
        // can render a special label (black/yellow badge instead of Hot Deal)
        const onBlackFridayPage = router?.pathname === '/Black-Friday-2025';
        let targetUrl = localePath(`/${sku.slug}`, locale);
        if (onBlackFridayPage) {
          targetUrl += '?bf=1';
        } else if (onEasterPage) {
          targetUrl += '?easter=1';
        } else if (inEndOfSummerSale) {
          targetUrl += `?${END_OF_SUMMER_SALE_QUERY_FLAG}=1`;
        }
        setIsNavigating(true);
        window.location.href = targetUrl;
      }
    }
  };

  const handleWishlistClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    toggleFromWishlist(sku as SkuDTO);
  };

  useEffect(() => {
    if (sku) {
      const currentDate = new Date();
      const skuReleaseDate = sku.releaseDate ? new Date(sku.releaseDate) : null;
      setReleaseDate(
        skuReleaseDate && skuReleaseDate.getTime() > currentDate.getTime() ? sku.releaseDate : null
      );
    }
  }, [sku]);

  // Unified responsive card
  return (
    <div className="tw-group tw-relative tw-h-full">
      {/* Rabatt-Badge links oben, falls Retail-Preis durchgestrichen wird */}
      {discount != null ? (
        <div className="tw-absolute tw-left-3 tw-top-3 tw-z-30 tw-rounded-[20px] tw-bg-[#ef4444] tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-shadow-md max-nine:tw-px-2 max-nine:tw-py-1 max-nine:tw-text-xs">
          -{discount}%
        </div>
      ) : (
        /* Release Date Badge - Desktop only (nur anzeigen, wenn kein Rabatt-Badge) */
        releaseDate && (
          <CommonTooltip
            id={`release-date-${sku?.id}`}
            title={new Date(releaseDate).toLocaleDateString(locale, {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          >
            <div className="tw-absolute tw-left-2 tw-top-2 tw-z-20 tw-hidden tw-cursor-pointer tw-rounded-[20px] tw-bg-gray-100 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-gray-800 tw-shadow-md md:tw-block">
              <time dateTime={releaseDate}>
                <span>{new Date(releaseDate).toLocaleString(locale, { month: 'short' })}</span>
                <span className="tw-mx-2 tw-text-gray-400">|</span>
                <span>{new Date(releaseDate).getDate()}</span>
              </time>
            </div>
          </CommonTooltip>
        )
      )}

      {/* Wishlist Button */}
      <div className="tw-absolute tw-right-2 tw-top-2 tw-z-10">
        {sku === null ? (
          <>
            <div className="tw-hidden md:tw-block">
              <WishlistButtonSkeleton />
            </div>
          </>
        ) : (
          <button
            className={clsx(
              // Mobile styles
              'tw-flex tw-aspect-square tw-h-auto tw-w-[23%] tw-max-w-20 tw-appearance-none tw-items-center tw-justify-center tw-border-none tw-bg-transparent',
              // Desktop styles
              'md:tw-h-10 md:tw-w-10 md:tw-rounded-full md:tw-bg-white/90 md:tw-shadow-md md:tw-backdrop-blur-sm',
              'md:tw-border-0 md:tw-transition-all md:tw-duration-200 md:tw-ease-in-out',
              'md:hover:tw-scale-105 md:hover:tw-bg-white md:hover:tw-shadow-lg',
              'md:tw-ring-blue-500 md:tw-ring-offset-2 md:focus:tw-outline-none md:focus:tw-ring-2'
            )}
            aria-label={isInWishlist(sku?.slug ?? '') ? 'Remove from wishlist' : 'Add to wishlist'}
            onClick={handleWishlistClick}
            type="button"
          >
            <FontAwesomeIcon
              icon={isInWishlist(sku?.slug ?? '') ? solidFaHeart : faHeart}
              className={clsx(
                // Mobile styles
                'tw-text-base',
                isInWishlist(sku?.slug ?? '') ? 'text-danger' : 'text-muted',
                // Desktop styles
                'md:tw-text-lg md:tw-transition-colors md:tw-duration-200',
                isInWishlist(sku?.slug ?? '') ? 'md:tw-text-red-500' : 'md:tw-text-gray-400'
              )}
            />
          </button>
        )}
      </div>

      {/* Main Card */}
      <Card
        as="a"
        href={sku?.slug ? localePath(`/${sku.slug}`, locale) : '#'}
        aria-label={sku ? `View ${sku.name} details` : 'Product loading'}
        className={clsx(
          // Mobile styles
          'tw-solid tw-relative tw-flex !tw-h-full !tw-min-h-[282px] tw-w-full tw-flex-col tw-gap-[0.5rem] tw-rounded-[10px] tw-border tw-border-custom-gray tw-p-[.8rem] tw-text-xs-custom tw-text-black tw-no-underline',
          // Desktop styles
          'md:tw-min-h-auto md:tw-block md:tw-h-full md:tw-gap-0 md:tw-overflow-hidden md:tw-rounded-[20px] md:tw-border md:tw-border-gray-200 md:tw-bg-white md:tw-p-0 md:tw-text-inherit md:tw-no-underline md:tw-shadow-md md:tw-transition-all md:tw-duration-300 md:tw-ease-in-out',
          // Tap feedback (mobile has no hover)
          'tw-transition-transform tw-duration-150 active:tw-scale-[0.97]',
          {
            'tw-pointer-events-none': isNavigating,
            'tw-pointer-events-none tw-overflow-hidden': !sku,
            'md:tw-opacity-75': !sku,
            'md:hover:tw-scale-[1.02] md:hover:tw-border-gray-300 md:hover:tw-shadow-xl': sku,
          }
        )}
        tabIndex={sku ? 0 : -1}
        onClick={handleSkuCardClick}
      >
        {/* Product Image */}
        <div className="tw-h-[130px] tw-w-full md:tw-relative md:tw-aspect-square md:tw-h-auto md:tw-overflow-hidden">
          {sku === null ? (
            <>
              <div className="tw-hidden md:tw-block">
                <SkuImageSkeleton />
              </div>
            </>
          ) : (
            <>
              {/* Mobile Image */}
              <div className="tw-h-[130px] tw-w-full tw-object-contain md:tw-hidden">
                <SkuMainImage
                  width={144}
                  height={130}
                  alt={sku?.name ?? ''}
                  loader={imageLoader}
                  style={CONTAIN_STYLE}
                  src={sku.mainImageUrl as string}
                />
              </div>
              {/* Desktop Image */}
              <div className="tw-hidden md:tw-block">
                <SkuCardImage sku={sku} />
              </div>
            </>
          )}
        </div>

        {/* Product Details */}
        <div className="skucarddesc tw-mt-3 tw-min-h-[53px] md:tw-mt-0 md:tw-flex md:tw-min-h-[85px] md:tw-flex-grow md:tw-flex-col md:tw-p-4 md:tw-pb-0">
          {/* Product Name */}
          <div>
            {sku === null ? (
              <>
                <div className="tw-hidden md:tw-block">
                  <SkuNameSkeleton />
                </div>
              </>
            ) : (
              <strong className="tw-mb-1 tw-line-clamp-2 tw-pb-[2px] tw-text-[14px] tw-font-semibold tw-leading-[16.38px] tw-text-[#1A202C] md:tw-mb-0 md:tw-text-base md:tw-font-bold md:tw-leading-tight md:tw-text-gray-900">
                {sku.name}
              </strong>
            )}
          </div>

          {/* Product Description */}
          <div className="md:tw-mb-0">
            {sku === null ? (
              <>
                <div className="tw-hidden md:tw-block">
                  <SkuDescriptionSkeleton />
                </div>
              </>
            ) : (
              <div className="tw-text-plusJakartaSans tw-text-[12px] tw-font-normal tw-leading-[13.86px] tw-text-slate-gray md:tw-line-clamp-2 md:tw-text-sm md:tw-text-gray-600">
                <SkuCardDescription sku={sku} />
              </div>
            )}
          </div>
        </div>

        {sku !== null ? (
          <div className="tw-mt-0 md:tw-mt-0 md:tw-flex md:tw-flex-grow md:tw-flex-col md:tw-p-4 md:tw-pt-0">
            <p className="tw-m-0 tw-p-0 tw-text-left tw-text-sm tw-font-normal tw-leading-normal tw-text-muted max-nine:tw-text-xs-lower">
              {isSellerCard
                ? tCommon('seller_ask')
                : showHighestBid
                  ? tSku(SkuEnum['buy.highest_bid'])
                  : tSku(CommonEnum['lowest_ask'])}
            </p>
            <div
              className={`tw-flex tw-w-full tw-items-center tw-justify-between max-nine:tw-flex-row max-nine:tw-items-center max-nine:tw-justify-between max-nine:tw-gap-2`}
            >
              {isSellerCard ? (
                <div className="tw-flex tw-gap-1">
                  {sku.hot_deal && sku.retailPrice !== null && sku.retailPrice > 0 && (
                    <span className="tw-flex tw-items-center tw-justify-center tw-text-gray-500 tw-line-through">
                      {toDe(sku.retailPrice)}
                    </span>
                  )}
                  <p className="max-nine: tw-mb-0 tw-text-[22px] tw-font-bold tw-text-textBlack max-nine:tw-text-base max-nine:tw-font-[600]">
                    {!askValue ? '--' : toDe(askValue)}
                  </p>
                </div>
              ) : (
                <>
                  <SkuPriceDisplay
                    sku={sku}
                    showHighestBid={showHighestBid}
                    forceEaster={onEasterPage}
                    forceEndOfSummerSale={inEndOfSummerSale}
                  />
                  <SkuTrendingBadge
                    sku={sku}
                    forceEaster={onEasterPage}
                    forceEndOfSummerSale={inEndOfSummerSale}
                  />
                </>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="tw-hidden md:tw-block">
              <SkuPriceSkeleton />
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
